import {
  ModFilledCanceled,
  ModFilledError,
  ModFilledInfo,
  ModFilledNeutral,
  ModFilledSuccess,
  ModFilledWarning,
  ModRipplingDot,
  ModVibratingDot
} from '@moderneinc/design-system-components';
import { semanticColors } from '@moderneinc/design-system-tokens';
import type { SvgIconProps, Theme } from '@mui/material';
import type { FunctionComponent } from 'react';
import { SpinningLoaderIcon } from './animated-icons';
import {
  CircleCheckIcon,
  CircleMinusIcon,
  CircleXIcon,
  ClockIcon,
  DebugIcon,
  InfoIcon,
  OctagonAlertIcon,
  OriginInfoIcon,
  SearchIcon,
  TriangleAlertIcon
} from './icons';

type IconAssets = {
  color?: SvgIconProps['color'];
  sxColor?: string | ((theme: Theme) => string);
  icon: FunctionComponent;
  /** Glyph for the pill status badge; matches `icon` where no outline exists. */
  outlineIcon: FunctionComponent;
  fill?: 'none' | 'currentColor';
};

export enum CriticalityLevel {
  NONE = 0,
  INFO = 1,
  DEBUG = 2,
  WARNING = 3,
  ERROR = 4
}

export const assetKeys = [
  'AVAILABLE',
  'BLOCKED',
  'CANCELED',
  'CLOSED',
  'COMPLETED',
  'CONNECTED',
  'CREATED',
  'DEBUG',
  'DISCONNECTED',
  'DRAFT',
  'ERROR',
  'FAILED',
  'FINISHED',
  'FINISHED_EMPTY',
  'INFO',
  'IN_PROGRESS',
  'LOADING',
  'MERGEABLE',
  'MERGED',
  'NO_CHANGES',
  'NO_LST',
  'NONE',
  'OPEN',
  'ORIGIN',
  'PARTIALLY_FAILED',
  'PROCESSING',
  'QUEUED',
  'RUNNING',
  'SYNCING',
  'SEARCH_RESULT',
  'SKIPPED',
  'SUCCESS',
  'UNAVAILABLE',
  'UNKNOWN',
  'VERIFIED',
  'WARNING',
  'YIELDED'
] as const;

export type AssetKey = (typeof assetKeys)[number];

type PossibleAssetKey = CriticalityLevel | AssetKey;

/**
 * We used to force things to uppercase if the key was a string. I can't
 * remember why so we've removed that and hopefully nothing breaks which is
 * really just not returning any icon and inherited color.
 */
const neutralSxColor = semanticColors.color.status.neutral.solid;

export const getIconAssetsByKey = (key: PossibleAssetKey): IconAssets => {
  switch (key) {
    case 'AVAILABLE':
    case 'OPEN':
    case 'INFO':
    case CriticalityLevel.INFO:
      return {
        color: 'info',
        sxColor: semanticColors.color.status.info.solid,
        icon: ModFilledInfo,
        outlineIcon: InfoIcon
      };
    case 'DEBUG':
    case CriticalityLevel.DEBUG:
      return {
        color: 'info',
        sxColor: (theme) => theme.palette.info.main,
        icon: DebugIcon,
        outlineIcon: DebugIcon,
        fill: 'currentColor'
      };
    case 'NO_LST':
    case 'WARNING':
    // Some repositories succeeded and some failed — a partial result rather
    // than a failure, matching getStatusBadgeColor.
    case 'PARTIALLY_FAILED':
    case CriticalityLevel.WARNING:
      return {
        color: 'warning',
        sxColor: semanticColors.color.status.warning.solid,
        icon: ModFilledWarning,
        outlineIcon: TriangleAlertIcon
      };
    case 'DISCONNECTED':
    case 'CLOSED':
    case 'ERROR':
    case 'FAILED':
    case 'BLOCKED':
    case CriticalityLevel.ERROR:
      return {
        color: 'error',
        sxColor: semanticColors.color.status.error.solid,
        icon: ModFilledError,
        outlineIcon: OctagonAlertIcon
      };
    case 'VERIFIED':
    case 'CONNECTED':
    case 'MERGED':
    case 'SUCCESS':
    case 'FINISHED':
    case 'COMPLETED':
    case 'MERGEABLE':
      return {
        color: 'success',
        sxColor: semanticColors.color.status.success.solid,
        icon: ModFilledSuccess,
        outlineIcon: CircleCheckIcon
      };
    case 'SKIPPED':
    case 'DRAFT':
    case 'FINISHED_EMPTY':
    case 'NO_CHANGES':
    case 'UNKNOWN':
    case 'UNAVAILABLE':
      return {
        color: undefined,
        sxColor: neutralSxColor,
        icon: ModFilledNeutral,
        outlineIcon: CircleMinusIcon
      };
    case 'CANCELED':
      return {
        color: undefined,
        sxColor: semanticColors.color.status.canceled.solid,
        icon: ModFilledCanceled,
        outlineIcon: CircleXIcon
      };
    case 'YIELDED':
    case 'LOADING':
      return {
        color: 'secondary',
        sxColor: (theme) => theme.palette.secondary.main,
        icon: ModRipplingDot,
        outlineIcon: SpinningLoaderIcon
      };
    case 'RUNNING':
    case 'SYNCING':
    case 'IN_PROGRESS':
    case 'PROCESSING':
      return {
        color: undefined,
        sxColor: semanticColors.color.status.info.solid,
        icon: ModRipplingDot,
        outlineIcon: SpinningLoaderIcon
      };
    case 'CREATED':
    case 'QUEUED':
      return {
        color: undefined,
        sxColor: semanticColors.color.status.queued.solid,
        icon: ModVibratingDot,
        outlineIcon: ClockIcon
      };

    case 'SEARCH_RESULT':
      return {
        color: 'primary',
        // The blue global search highlights matches with.
        sxColor: semanticColors.color.action.default,
        icon: SearchIcon,
        outlineIcon: SearchIcon
      };

    case 'ORIGIN':
      return {
        color: 'inherit',
        sxColor: (theme) => theme.palette.grey[600],
        icon: OriginInfoIcon,
        outlineIcon: OriginInfoIcon
      };

    case CriticalityLevel.NONE:
    default:
      return {
        color: 'inherit',
        sxColor: 'inherit',
        icon: () => null,
        outlineIcon: () => null
      };
  }
};
